import type { PdpOrderBy, SortDirection } from '@vizit/api';
import { TableHeader } from '@vizit/atoms';
import { FEATURE_FLAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import * as React from 'react';

interface ProductListingTableTheadProps {
  /**
   * Whether to show the action column
   * @default false
   */
  showActionColumn?: boolean;
  /**
   * The field currently being sorted
   * @optional
   */
  sortBy?: PdpOrderBy['field'];
  /**
   * The current sort direction
   * @optional
   */
  sortDirection?: SortDirection;
  /**
   * Callback when a sortable column header is clicked
   * @optional
   */
  onSort?: (field: PdpOrderBy['field']) => void;
}

// Centres the label over the centred scores. The sort arrow hangs off the label's right edge, outside the flow, so the
// label doesn't move when the arrow appears (e.g. the skeleton header has no arrow, the loaded one does).
const centeredSortableColumnClasses =
  'whitespace-nowrap w-80 text-center [&>button]:relative [&>button]:mx-auto [&>button>svg]:absolute [&>button>svg]:left-full';

const ProductListingTableThead = React.memo((props: ProductListingTableTheadProps) => {
  const { showActionColumn = false, sortBy, sortDirection, onSort } = props;
  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.APRIL_SCORING_CHANGES, false);

  const getSortDirection = (field: PdpOrderBy['field']): SortDirection | false => {
    if (sortBy === field && sortDirection) return sortDirection;
    return false;
  };

  return (
    <TableHeader>
      {/* No width: the product column takes whatever the fixed-width columns leave */}
      <TableHeader.Column isRowHeader>Product Page</TableHeader.Column>
      {isRetailerEnabled && (
        <TableHeader.Column className="whitespace-nowrap w-60 text-center">Retailer</TableHeader.Column>
      )}
      <TableHeader.SortableColumn
        className={centeredSortableColumnClasses}
        sortDirection={getSortDirection('listing_score_at_ingest')}
        onSort={() => onSort?.('listing_score_at_ingest')}
      >
        Live score
      </TableHeader.SortableColumn>
      <TableHeader.SortableColumn
        className={centeredSortableColumnClasses}
        sortDirection={getSortDirection('listing_score')}
        onSort={() => onSort?.('listing_score')}
      >
        Your score
      </TableHeader.SortableColumn>
      <TableHeader.Column className="whitespace-nowrap w-56">Change</TableHeader.Column>
      {/* Fits the two hover actions (refresh + delete) plus the cell padding */}
      {showActionColumn && <TableHeader.Column className="w-56" />}
    </TableHeader>
  );
});

export { ProductListingTableThead };
export type { ProductListingTableTheadProps };
